import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {TimelineLite, TweenLite} from "gsap";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {HorribleHeroesParsedGameData} from "../../server/data/HorribleHeroesParsedGameData";
import {HorribleHeroesGameConfig} from "../../HorribleHeroesGameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {SymbolUtil} from "../../utils/SymbolUtil";
import {StateMatrix} from "../../utils/StateMatrix";
import {HorribleHeroesGameModuleConfig} from "../../HorribleHeroesGameModuleConfig";
import {HorribleHeroesReelSymbol} from "../../symbols/HorribleHeroesReelSymbol";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {SoundManager} from "../../resource/sound/SoundManager";
import {HorribleHeroes} from "../../HorribleHeroes";

export class BetWayWin implements GameRoundPresentation {
    private _gameData: HorribleHeroesParsedGameData;

    private readonly _isFirstTime: boolean = true;
    private _hasWin: boolean = false;
    private _symbolStates: StateMatrix<string>;
    private _delayAdded: boolean = false;
    private _isAbort: boolean = false;
    private _tl: TimelineLite;

    /**
     * @constructs
     */
    constructor(forInitialWin: boolean = false) {
        this._isFirstTime = forInitialWin;
    }

    private static resetToNormal(coords: number[]): void {
        let sym = Reels.getSymbol(coords[0], coords[1])!;
        sym!.changeState({
            state: HorribleHeroesGameConfig.instance.SYMBOL_STATES.normal,
            setStack: true,
            fadeInDuration: 0
        });
    }

    public prepare(data: HorribleHeroesParsedGameData): void {
        this._gameData = data;
        this._hasWin = this._gameData.singleWin > 0 && this._gameData.betWins.length > 0;

        // #463
        this._isAbort = false;

        // #582
        HorribleHeroesGameConfig.instance.WIN_BELOW_STAKE = (HorribleHeroes.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));
    }

    public getAnimation(data: HorribleHeroesParsedGameData): TimelineLite | null {
        if (!this._hasWin) {
            return null;
        }

        this._tl = new TimelineLite({
            onComplete: () => {
                this._tl.pause();
                this._tl.kill();
            }
        });

        // #300
        if (!this._delayAdded && !this._isFirstTime) {
            // #318 -> 1st point
            this._tl.delay((HorribleHeroesGameConfig.instance.ABORT_DELAY + 0.11)); // 0.11 is -> auto play interval is 100ms in the framework.
            this._delayAdded = true;
        }

        this._symbolStates = SymbolUtil.createSymbolStateMatrixFromBetWins(
            <IBetWayWinData[]>data!.betWins!,
            HorribleHeroesGameConfig.instance.SYMBOL_STATES.noWin!.keyword,
            HorribleHeroesGameConfig.instance.SYMBOL_STATES.win!.keyword);

        if (this._isFirstTime) {
            this._tl.add(() => HorribleHeroesGameModuleConfig.winTextView.hideToggleText());
            //#404
            this._tl.add(this.getWinSymbolAnimations(data, this._isFirstTime));
        } else {
            let dataLength: number = this._gameData.betWins.length;
            for (let i: number = 0; i < dataLength; i++) { //0//1
                if (i === 0) {
                    this._tl.add(() => this.hideAllAnimations(), 0);
                }

                if (dataLength > 1) { //2
                    this._tl.add(() => { //0//1.6
                        let fadeIn = true;
                        let fadeOut = false;
                        HorribleHeroesGameModuleConfig.symbolWinAnimation.resetAnimation();
                        if (i > 0) {
                            fadeIn = false;
                        }
                        this.getBetWaysToggleAnimation((this._gameData.betWins[i] as IBetWayWinData), fadeIn, fadeOut);
                    }, 1.5 * i);

                    if (i === dataLength - 1) {
                        this._tl.add(() => {
                            this.getWinSymbolAnimations(data);
                        }, dataLength * 1.5);

                        this._tl.add(() => {//#406
                            HorribleHeroesGameModuleConfig.winTextView.toggleWayText(this._gameData.getNumberOfWaysWon, this._gameData.singleWin, false);
                        }, (dataLength * 1.5) + 0.1);

                        this._tl.add(() => {
                            HorribleHeroesGameModuleConfig.winTextView.fadeOutToggleText();
                        }, (dataLength * 1.5) + 1.3);
                        this._tl.add(() => {
                            this.hideAllAnimations();
                        }, (dataLength * 1.5) + 1.5);
                        this._tl.add(() => {
                        }, (dataLength * 1.5) + 3);
                    }
                } else {
                    this._tl.add([
                        this.getBetWaysToggleAnimation((this._gameData.betWins[i] as IBetWayWinData), true, true)
                    ], 1.5);
                }
            }
        }
        return this._tl;
    }

    public abort(isActive: boolean, data: HorribleHeroesParsedGameData): TimelineLite | null {
        this._isAbort = isActive;
        const tl: TimelineLite = new TimelineLite();
        if (this._isFirstTime) {
            tl.add(this.getWinSymbolAnimations(data, this._isFirstTime));
        }

        this._delayAdded = false;

        return tl;
    }

    private hideAllAnimations(): void {
        HorribleHeroesGameModuleConfig.winTextView.hideToggleText();
        HorribleHeroesGameModuleConfig.countUpView.hideCountUp();//#509
        HorribleHeroesGameModuleConfig.symbolWinAnimation.resetAnimation();
        if (this._gameData.getGameAreaSizeAfter) {
            for (let j = 0; j < this._gameData.getGameAreaSizeAfter.length; j++) {
                for (let i = 0; i < this._gameData.getGameAreaSizeAfter[j]!; i++) {
                    const sym = <HorribleHeroesReelSymbol>Reels.getSymbol(j, i)!;
                    if (sym!) {
                        sym!.stopPotentialWin();
                    }
                    BetWayWin.resetToNormal([j, i]);
                }
            }
        }
    }

    private hideFireOnAllSyms(data: HorribleHeroesParsedGameData): void {
        data.getPotentialSymbolPostions.forEach((pos: boolean[], i: number) => {
            for (let j = 0; j < data.getGameAreaSizeAfter[i]; j++) {
                const sym = <HorribleHeroesReelSymbol>Reels.getSymbol(i, j)!;
                if (sym!) {
                    sym!.stopPotentialWin();
                    sym!.stopHighlightWin();
                }
            }
        });
    }

    private getWinSymbolAnimations(data: HorribleHeroesParsedGameData, InitialWin?: boolean): TimelineLite | null {
        let pos: number = 0.2;
        //#287
        HorribleHeroesGameModuleConfig.symbolWinAnimation.resetAnimation();
        this.hideFireOnAllSyms(data);
        const tl: TimelineLite = new TimelineLite(),
            seedPosInitialWin: number = 0.35,
            isTinyOrSmall: boolean = (data.currentWinRatioConfig!.duration === 0); // #331

        // #362
        if (InitialWin) {
            tl.add(() => {
                // #492
                if (data.brokeTheBank) {
                    SoundManager.playCapWinBetlineSweep();
                } else if (!data.isBigWin && !HorribleHeroesGameConfig.instance.WIN_BELOW_STAKE) { // #582
                    let betlineSweepIndex: number;
                    switch (data.currentWinRatioConfig!.type) {
                        case WinRatioType.SMALL:
                            betlineSweepIndex = 1;
                            break;
                        case WinRatioType.MEDIUM:
                            betlineSweepIndex = 2;
                            break;
                        case WinRatioType.LARGE:
                            betlineSweepIndex = 3;
                            break;
                        default:
                            betlineSweepIndex = 0;
                    }

                    SoundManager.playBetlineSweep(betlineSweepIndex);
                }
            }, seedPosInitialWin);
        }

        for (let reelId: number = 0; reelId < HorribleHeroesGameConfig.instance.REELS_NUM; reelId++) {
            const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, HorribleHeroesGameConfig.instance.SYMBOL_STATES.win!.keyword);
            const reelnoWinCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, HorribleHeroesGameConfig.instance.SYMBOL_STATES.noWin!.keyword);
            for (let coords of reelCoords) {
                if (InitialWin) {
                    if (!this._isAbort) {
                        pos = seedPosInitialWin + (reelId * 0.05); // to fix #337 after impl. #331
                    } else {
                        pos = 0.2;
                        //#404
                    }
                }
                tl.add([
                    () => {
                        let sym = Reels.getSymbol(coords[0], coords[1])!;
                        if (sym!) {
                            sym!.changeState({
                                state: HorribleHeroesGameConfig.instance.SYMBOL_STATES.win!,
                                setStack: true,
                                fadeInDuration: 0
                            });
                        }
                    },
                    HorribleHeroesGameModuleConfig.symbolWinAnimation.playSymbolWinAnimation(coords[0], coords[1], data.getGameAreaSizeAfter[reelId], !isTinyOrSmall, !isTinyOrSmall)
                ], pos);
            }
            //#544(2nd scenario)
            for (let noWinCoords of reelnoWinCoords) {
                tl.add([
                    () => {
                        let sym = Reels.getSymbol(noWinCoords[0], noWinCoords[1])!;
                        if (sym!) {
                            sym!.changeState({
                                state: HorribleHeroesGameConfig.instance.SYMBOL_STATES.noWin!,
                                setStack: true,
                                fadeInDuration: 0
                            });
                        }
                    },
                ], pos);
            }
        }
        return tl;
    }

    private getBetWaysToggleAnimation(data: IBetWayWinData, fadein?: boolean, fadeOut?: boolean): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            let waysWon: number = data.numberOfCombinations,
                reelPos: number[][] = [],
                noWinPos: number[][] = [];
            data.reelWinPositions.forEach((reelB, reelId) => {
                reelB.forEach((isWin, symId) => {
                    if (isWin) {
                        reelPos.push([reelId, symId]);
                    } else {
                        noWinPos.push([reelId, symId]);
                    }
                })
            });

            for (let coords of noWinPos) {
                let sym = Reels.getSymbol(coords[0], coords[1])!;
                sym!.changeState({
                    state: HorribleHeroesGameConfig.instance.SYMBOL_STATES.noWin!,
                    setStack: true,
                    fadeInDuration: 0.0
                });
            }
            for (let coords of reelPos) {
                let sym = Reels.getSymbol(coords[0], coords[1])!;
                sym!.changeState({
                    state: HorribleHeroesGameConfig.instance.SYMBOL_STATES.win!,
                    setStack: true,
                    fadeInDuration: 0.0
                });
                HorribleHeroesGameModuleConfig.symbolWinAnimation.playSymbolWinAnimation(coords[0], coords[1], this._gameData.getGameAreaSizeAfter[coords[0]], true)
            }
            HorribleHeroesGameModuleConfig.winTextView.toggleWayText(waysWon, data.multipliedAmount, fadein);
        });
        // need to add this empty tween so that the symbol set back to its normal state between the toggling.
        tl.add(TweenLite.to(() => {
            //blank tween
        }, 0.1, {}), 0.9);
        tl.add(() => {
            if (fadeOut) {
                HorribleHeroesGameModuleConfig.winTextView.fadeOutToggleText();
            }
        }, 1.3);
        tl.add(() => {
            //let it fadeout
        }, 1.5);
        return tl;
    }
}